import { useEffect, useMemo, useState } from "react";

import { COORDINATE_SPACE } from "@perceptron/utils/math";

import {
  BoxAnnotation,
  isBoxAnnotation,
  Annotation,
  isPointAnnotation,
  isPolygonAnnotation,
} from "../../types/annotations";
import { Point } from "../../types/coordinates";

const coordsToSvgPoints = (coords: Point[]): string => {
  return coords.map((point) => `${point.x},${point.y}`).join(" ");
};

const getBoxCoordinates = (shape: BoxAnnotation): Point[] => {
  const { top_left, bottom_right } = shape.coordinates;
  const horizontalDistance = bottom_right.x - top_left.x;
  const verticalDistance = bottom_right.y - top_left.y;

  return [
    top_left,
    { x: top_left.x, y: top_left.y + verticalDistance },
    bottom_right,
    { x: top_left.x + horizontalDistance, y: top_left.y },
  ];
};

const createSvgMask = (shapes: string[], width: number, height: number): string => {
  const polygons = shapes.map((points) => `<polygon points="${points}" fill="black"/>`).join("");

  return `
        <svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">
          <defs>
            <mask id="combinedMask">
              <rect width="100%" height="100%" fill="white"/>
              ${polygons}
            </mask>
          </defs>
          <rect width="100%" height="100%" fill="white" mask="url(#combinedMask)"/>
        </svg>
      `.trim();
};

const useAnnotationMask = ({
  imageUrl,
  highlightedAnnotationIds,
  clickedAnnotationId,
  allFlattenedAnnotations,
  visibleAnnotationIds,
}: {
  imageUrl: string | undefined;
  highlightedAnnotationIds: string[] | undefined;
  clickedAnnotationId: string | undefined;
  allFlattenedAnnotations: Annotation[];
  visibleAnnotationIds: Set<string>;
}): string => {
  const [imageDimensions, setImageDimensions] = useState<{ width: number; height: number } | null>(null);

  const scaleCoordinates = useMemo(
    () =>
      (coords: Point[], imageWidth: number, imageHeight: number): Point[] => {
        return coords.map((point) => ({
          x: (point.x / COORDINATE_SPACE) * imageWidth,
          y: (point.y / COORDINATE_SPACE) * imageHeight,
        }));
      },
    [],
  );

  const getShapePoints = useMemo(
    () =>
      (shape: Annotation, imageDimensions: { width: number; height: number }): string | null => {
        if (isPointAnnotation(shape) || !shape.coordinates) return null;

        let coords: Point[];
        if (isBoxAnnotation(shape)) {
          coords = getBoxCoordinates(shape);
        } else if (isPolygonAnnotation(shape)) {
          coords = shape.coordinates;
        } else {
          return null;
        }

        return coordsToSvgPoints(scaleCoordinates(coords, imageDimensions.width, imageDimensions.height));
      },
    [scaleCoordinates],
  );

  useEffect(() => {
    if (!imageUrl) {
      setImageDimensions(null);
      return;
    }

    const img = new window.Image();
    img.src = imageUrl;

    const handleLoad = () => {
      setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    };

    const handleError = () => {
      console.error("Failed to load image:", imageUrl);
      setImageDimensions(null);
    };

    img.onload = handleLoad;
    img.onerror = handleError;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl]);

  const svgMask = useMemo(() => {
    if (allFlattenedAnnotations.length === 0 || !imageDimensions) {
      return "";
    }

    // Need either highlighted annotations or a clicked annotation to show mask
    if (!highlightedAnnotationIds && clickedAnnotationId === undefined) {
      return "";
    }

    const shapesToRender = allFlattenedAnnotations.filter((shape) => {
      if (!shape.coordinates) return false;

      // Only show mask for annotations that are currently visible (matches box/label visibility)
      if (!visibleAnnotationIds.has(shape.id)) return false;

      const isClicked = clickedAnnotationId === shape.id;
      const isHighlighted = highlightedAnnotationIds?.includes(shape.id) ?? false;
      const isBox = isBoxAnnotation(shape);
      const isZoomBox = isBox && (shape as BoxAnnotation).boxType === "zoom";

      // Zoom boxes: show mask only when clicked
      if (isZoomBox) {
        return isClicked;
      }

      // All other annotations (content and reasoning): show mask when clicked or highlighted
      return isClicked || isHighlighted;
    });

    if (shapesToRender.length === 0) {
      return "";
    }

    const svgShapes = shapesToRender
      .map((shape) => getShapePoints(shape, imageDimensions))
      .filter((points): points is string => !!points);

    if (svgShapes.length === 0) return "";

    const svgMaskString = createSvgMask(svgShapes, imageDimensions.width, imageDimensions.height);
    const encodedSvg = encodeURIComponent(svgMaskString);

    return `url("data:image/svg+xml,${encodedSvg}")`;
  }, [
    allFlattenedAnnotations,
    highlightedAnnotationIds,
    clickedAnnotationId,
    imageDimensions,
    getShapePoints,
    visibleAnnotationIds,
  ]);

  return svgMask;
};

export default useAnnotationMask;
